import { Collapsible } from "@/components/ui";
import { Separator } from "@/components/ui/Separator";
import { BodyText } from "@/components/ui/Text/BodyText";
import { useMemo } from "react";
import type {
  DxReportViewResponse,
  ReportComment,
} from "../../network/useDxReportView";

export const ReportNotes = ({
  dxReportView,
}: { dxReportView: DxReportViewResponse }) => {
  const { comments } = dxReportView;

  const { tnpTests, qnsTests } = useMemo(() => {
    const tnpTests: Record<
      string,
      { testName: string; comments: ReportComment[] }
    > = {};
    const qnsTests: Record<
      string,
      { testName: string; comments: ReportComment[] }
    > = {};

    for (const test of dxReportView.tests) {
      if (!test.curaiServiceRequestId || !test.testName) {
        continue;
      }
      for (const result of test.testResults) {
        if (
          result.resultRawValue?.valueString?.toUpperCase()?.trim() === "TNP"
        ) {
          if (!tnpTests[test.curaiServiceRequestId]) {
            tnpTests[test.curaiServiceRequestId] = {
              testName: test.testName,
              comments: comments,
            };
          }
          tnpTests[test.curaiServiceRequestId].comments.push(
            ...result.resultComments,
          );
        }
        if (
          result.resultRawValue?.valueString?.toUpperCase()?.trim() === "QNS"
        ) {
          if (!qnsTests[test.curaiServiceRequestId]) {
            qnsTests[test.curaiServiceRequestId] = {
              testName: test.testName,
              comments: comments,
            };
          }
          qnsTests[test.curaiServiceRequestId].comments.push(
            ...result.resultComments,
          );
        }
      }
    }

    return { tnpTests, qnsTests };
  }, [dxReportView.tests, comments]);

  if (
    !comments?.length &&
    Object.keys(tnpTests).length === 0 &&
    Object.keys(qnsTests).length === 0
  ) {
    return null;
  }

  return (
    <Collapsible title="Notes" open={true}>
      {comments?.length > 0 && (
        <div className="mb-2">
          <BodyText bold size="sm">
            Report Comments
          </BodyText>
          {comments?.map((comment, idx) => (
            <div key={`notes-${comment.createdAt}`}>
              <BodyText className="font-mono whitespace-pre-wrap" size="sm">
                {comment.comment}
              </BodyText>
              {idx !== comments.length - 1 && <Separator />}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {tnpTests && (
          <div className="mb-2">
            <BodyText bold size="sm">
              Test Not Performed (TNP)
            </BodyText>
            {Object.entries(tnpTests).map(([serviceRequestId, data]) => (
              <div key={serviceRequestId} className="flex flex-col gap-1">
                <BodyText size="sm">{data.testName}</BodyText>

                {data.comments.map((comment) => (
                  <BodyText
                    size="sm"
                    className="font-mono whitespace-pre-wrap"
                    key={`${serviceRequestId}-${comment.createdAt}`}
                  >
                    {comment.comment}
                  </BodyText>
                ))}
              </div>
            ))}
          </div>
        )}
        {qnsTests && (
          <div className="mb-2">
            <BodyText bold size="sm">
              Quantity Not Sufficient (QNS)
            </BodyText>
            {Object.entries(qnsTests).map(([serviceRequestId, data]) => (
              <div key={serviceRequestId} className="flex flex-col gap-1">
                <BodyText size="sm">{data.testName}</BodyText>

                {data.comments.map((comment) => (
                  <BodyText
                    size="sm"
                    className="font-mono whitespace-pre-wrap"
                    key={`${serviceRequestId}-${comment.createdAt}`}
                  >
                    {comment.comment}
                  </BodyText>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </Collapsible>
  );
};
